import React, { useMemo } from 'react';
import { Collapse, Table, Tag, Typography, Empty, Space } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import odooOperationSchemas from '../data/odooOperationSchemas';

const { Panel } = Collapse;
const { Paragraph, Text } = Typography;

/**
 * 顯示某個 operationType 的 cheat-sheet：master / lines / FK / 範例 JSON / tips。
 *
 * Props：
 *  - operationType: 'createSO' | 'createPO' | ...
 *  - t: i18n function（可選，缺省時用 schema 內 label）
 */
const OdooOperationSchemaPanel = ({ operationType, t }) => {
  const schema = useMemo(() => odooOperationSchemas[operationType] || null, [operationType]);

  const tt = (key, fallback) => (typeof t === 'function' ? (t(key) || fallback) : fallback);

  if (!schema) {
    return (
      <Empty
        description={tt('workflowDesigner.odoo.schemaPanelMissing',
          `沒有 ${operationType || 'unknown'} 對應的 cheat-sheet`)}
        style={{ margin: '8px 0' }}
      />
    );
  }

  const renderField = (record) => (
    <Space size={4} wrap>
      <Text code>{record.path}</Text>
      {record.required && <Tag color="red">{tt('workflowDesigner.odoo.requiredTag', 'required')}</Tag>}
      {record.fk && <Tag color="blue">{tt('workflowDesigner.odoo.fkTag', 'FK')}: {record.fk}</Tag>}
      {record.type && <Tag>{record.type}</Tag>}
    </Space>
  );

  const fieldColumns = [
    {
      title: tt('workflowDesigner.odoo.schemaPath', '路徑 / 標籤'),
      key: 'path',
      width: '55%',
      render: (_v, r) => (
        <div>
          {renderField(r)}
          <div style={{ color: '#888', fontSize: 12, marginTop: 2 }}>{r.label}</div>
        </div>
      )
    },
    {
      title: tt('workflowDesigner.odoo.schemaTip', '說明'),
      dataIndex: 'tip',
      render: (v) => v ? <Text style={{ fontSize: 12 }}>{v}</Text> : null
    }
  ];

  return (
    <Collapse
      bordered
      style={{ marginBottom: 12, background: '#fafafa' }}
    >
      <Panel
        key="cheat"
        header={
          <Space>
            <InfoCircleOutlined />
            <Text strong>{schema.label || operationType}</Text>
            <Tag>{schema.odooModel}</Tag>
            {schema.odooMethod && <Tag color="purple">{schema.odooMethod}</Tag>}
          </Space>
        }
      >
        {schema.master && schema.master.length > 0 && (
          <>
            <Text strong>{tt('workflowDesigner.odoo.schemaMaster', 'Master 欄位')}</Text>
            <Table
              size="small"
              rowKey="path"
              pagination={false}
              dataSource={schema.master}
              columns={fieldColumns}
              style={{ marginTop: 4, marginBottom: 12 }}
            />
          </>
        )}
        {schema.lines && schema.lines.length > 0 && (
          <>
            <Text strong>{tt('workflowDesigner.odoo.schemaLines', 'Line 欄位（每筆 row 重複）')}</Text>
            <Table
              size="small"
              rowKey="path"
              pagination={false}
              dataSource={schema.lines}
              columns={fieldColumns}
              style={{ marginTop: 4, marginBottom: 12 }}
            />
          </>
        )}
        {schema.fk && schema.fk.length > 0 && (
          <Paragraph style={{ marginBottom: 8 }}>
            <Text strong>{tt('workflowDesigner.odoo.schemaFk', 'FK 參照')}: </Text>
            {schema.fk.map((fk) => <Tag key={fk} color="blue">{fk}</Tag>)}
          </Paragraph>
        )}
        {schema.example && (
          <>
            <Text strong>{tt('workflowDesigner.odoo.schemaExample', '範例 JSON')}</Text>
            <pre
              style={{
                background: '#0e1117',
                color: '#d6e7ff',
                padding: 10,
                borderRadius: 4,
                fontSize: 12,
                marginTop: 4,
                marginBottom: 12,
                overflow: 'auto',
                maxHeight: 280
              }}
            >
              {JSON.stringify(schema.example, null, 2)}
            </pre>
          </>
        )}
        {schema.tips && schema.tips.length > 0 && (
          <>
            <Text strong>{tt('workflowDesigner.odoo.schemaTips', '注意事項')}</Text>
            <ul style={{ margin: '4px 0 0 0', paddingLeft: 20 }}>
              {schema.tips.map((tip, i) => (
                <li key={i} style={{ fontSize: 12 }}>{tip}</li>
              ))}
            </ul>
          </>
        )}
      </Panel>
    </Collapse>
  );
};

export default OdooOperationSchemaPanel;
